/**
 * Pistas de primer uso y ayuda.
 *
 * Existe por un problema concreto: la app estaba bonita y nadie entendía cómo usarla. Un botón ＋
 * grande no dice que repite la serie anterior, y una pila de placas no dice que cada placa es un
 * 5 % de lo que bajaste. La solución no es un tour de siete pasos que todos saltean, sino decir la
 * cosa **en el lugar donde está**, una vez, y no volver a molestar.
 *
 * Reglas de las pistas:
 * - Se descartan y no vuelven. La marca vive en localStorage.
 * - Nunca tapan un control. Van al lado o abajo, nunca encima.
 * - Dicen QUÉ hace algo, no cómo se llama.
 */

import { useCallback, useState, type ReactNode } from 'react';

import { Icono } from './componentes.tsx';
import type { Pantalla } from './router.tsx';

const CLAVE = 'mg.pistas';

function leerDescartadas(): Set<string> {
  try {
    const crudo = localStorage.getItem(CLAVE);
    return new Set(crudo ? (JSON.parse(crudo) as string[]) : []);
  } catch {
    return new Set();
  }
}

function guardarDescartadas(set: Set<string>): void {
  try {
    localStorage.setItem(CLAVE, JSON.stringify([...set]));
  } catch {
    // Sin localStorage la pista vuelve a aparecer en la próxima sesión. Es molesto, no roto.
  }
}

export function usePista(clave: string): { visible: boolean; descartar: () => void } {
  const [descartadas, setDescartadas] = useState<Set<string>>(leerDescartadas);

  const descartar = useCallback(() => {
    setDescartadas((prev) => {
      const siguiente = new Set(prev).add(clave);
      guardarDescartadas(siguiente);
      return siguiente;
    });
  }, [clave]);

  return { visible: !descartadas.has(clave), descartar };
}

/** Devuelve todas las pistas al estado inicial. Vive en Ajustes. */
export function reiniciarPistas(): void {
  try {
    localStorage.removeItem(CLAVE);
  } catch {
    /* nada que hacer */
  }
}

export function Pista({ clave, children }: { clave: string; children: ReactNode }): ReactNode {
  const { visible, descartar } = usePista(clave);
  if (!visible) return null;

  return (
    <div
      className="aviso aviso--info"
      style={{ display: 'flex', gap: 'var(--e3)', alignItems: 'flex-start' }}
      role="note"
    >
      <span style={{ flex: 1 }}>{children}</span>
      <button
        type="button"
        onClick={descartar}
        aria-label="Entendido, no mostrar más"
        title="Entendido"
        style={{
          background: 'none',
          border: 0,
          color: 'var(--tiza-media)',
          padding: 2,
          minHeight: 28,
          minWidth: 28,
          display: 'grid',
          placeItems: 'center',
          flex: 'none',
        }}
      >
        <Icono nombre="cerrar" tamano={14} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ayuda
// ---------------------------------------------------------------------------

export interface TemaDeAyuda {
  titulo: string;
  cuerpo: string;
  /**
   * Dónde hace falta saber esto. El botón ? de una pantalla abre sus temas y nada más: una ayuda
   * de siete temas obliga a buscar, y quien está parado frente a una máquina no busca.
   */
  pantallas: Pantalla[];
}

/**
 * Lo mínimo que hay que saber, en el orden en que hace falta. Corto a propósito: una ayuda que
 * nadie lee es lo mismo que no tener ayuda.
 */
export const AYUDA: TemaDeAyuda[] = [
  {
    // Va primero y aparece en Inicio porque era lo único que la app no decía en ninguna parte:
    // para qué sirve. Sin esto, las nueve pantallas son nueve pantallas sueltas.
    titulo: 'Para qué sirve esta app',
    cuerpo:
      'Hace dos cosas y las cruza. Una: anotás cada serie que hacés, y con eso te dice cuándo te toca subir la placa y qué músculo estás dejando de lado. Dos: te pesás en una balanza de bioimpedancia, y con eso te dice si lo que estás bajando es grasa o es músculo. Cruzarlas es el punto: bajar de peso perdiendo músculo no es el mismo resultado, y una balanza sola no te lo puede decir.',
    pantallas: ['inicio'],
  },
  {
    titulo: 'Registrar una serie',
    cuerpo:
      'En Entrenar, el botón ＋ de cada ejercicio registra una serie igual a la anterior: un toque y listo. Para cambiar el peso o las reps tocá Ajustar, y movés con los botones − y +, que suben de a un escalón real de esa máquina.',
    pantallas: ['registro'],
  },
  {
    // Lo que faltaba en Entrenar no era cómo empezar, era cómo terminar: la sesión quedaba abierta,
    // un ejercicio que no se pudo hacer se dejaba en blanco (y en blanco no es lo mismo que
    // omitido) y para corregir había que adivinar dónde.
    titulo: 'Cerrar, omitir y corregir',
    cuerpo:
      'Al terminar, Cerrá la sesión: te muestra el resumen y qué sigue. Si no pudiste hacer un ejercicio, abrilo y tocá No pude hacerlo — no cuenta como cero, se excluye. Para corregir algo ya guardado, Historial (en Más).',
    pantallas: ['registro'],
  },
  {
    titulo: 'Las barritas',
    cuerpo:
      'Cada barrita es una unidad, no un porcentaje suelto: una serie, una sesión, un 5 % de lo que bajaste. Las llenas son lo hecho, las huecas lo que falta, y las rojas algo que se va a perder. Se dibuja así porque en el gimnasio todo viene en unidades enteras — una máquina que sube de 5 en 5 no admite 2,5.',
    pantallas: ['inicio', 'registro', 'cuerpo', 'calendario', 'progreso'],
  },
  {
    titulo: 'Calidad de la pérdida',
    cuerpo:
      'Es el número grande del inicio: de todo el peso que bajaste, cuánto fue grasa. Arriba del 80 % va muy bien; abajo del 60 % estás perdiendo demasiado músculo. Necesita al menos 3 mediciones de balanza para poder calcularse — con menos, la app dice que le faltan datos en vez de inventar un número.',
    pantallas: ['inicio', 'cuerpo', 'progreso'],
  },
  {
    titulo: 'Pesarse bien',
    cuerpo:
      'Una balanza de bioimpedancia estima, no mide: entre la mañana y la noche el porcentaje de grasa se te mueve 2 a 4 puntos sin que hayas cambiado nada. Por eso la app nunca trata una lectura suelta como un hecho — la serie que ves es la media de 7 días y los puntos grises de atrás son las lecturas crudas. Para que sirva: pesate siempre a la misma hora, en ayunas y después de ir al baño, y anotá en qué condiciones fue.',
    pantallas: ['cuerpo', 'ingesta'],
  },
  {
    titulo: 'Comida',
    cuerpo:
      'Escribí lo que comiste como se lo contarías a alguien ("150 g de pollo con arroz") y la IA estima las calorías y la proteína. Revisás, corregís lo que esté mal y confirmás. Lo que estimó con poca certeza viene marcado en ámbar. La app registra lo que comés; no te dice cuánto tenés que comer.',
    pantallas: ['nutricion', 'ingesta'],
  },
  {
    titulo: 'Días de colchón',
    cuerpo:
      'Si compraste un paquete de sesiones con vencimiento, el colchón son los días de margen que te sobran. Cuando bajás de 2, la app avisa. En Plan podés tocar cualquier día y preguntar "¿y si falto?" para ver el efecto antes de decidir.',
    pantallas: ['calendario', 'inicio'],
  },
  {
    titulo: 'Sin señal',
    cuerpo:
      'Todo el registro funciona sin conexión. Lo que anotes se guarda en el teléfono al instante y sube solo cuando vuelve la señal, incluso si cerrás la app a la fuerza. Lo único que necesita red es leer una foto y estimar una comida.',
    // Comida y Foto son justo las dos que necesitan red, así que son las dos pantallas donde este
    // tema hace falta de verdad — y eran las dos donde no aparecía.
    pantallas: ['registro', 'nutricion', 'ingesta', 'ajustes'],
  },
  {
    titulo: 'Corregir',
    cuerpo:
      'Equivocarse es lo normal. En Historial podés cambiar el peso o las reps de una serie ya guardada, mover una sesión de fecha, y reasignar una serie al ejercicio correcto si la anotaste en el equivocado — sin borrar nada.',
    pantallas: ['historial', 'registro'],
  },
];

/**
 * Los temas que hacen falta en una pantalla. Si una pantalla no reclama ninguno se devuelven
 * todos: es preferible una lista larga a un panel vacío que hace parecer rota a la ayuda.
 */
export function temasDePantalla(pantalla: Pantalla): TemaDeAyuda[] {
  const propios = AYUDA.filter((t) => t.pantallas.includes(pantalla));
  return propios.length > 0 ? propios : AYUDA;
}

export function PanelDeAyuda({ compacto = false }: { compacto?: boolean }): ReactNode {
  return (
    <div className="columna" style={{ gap: compacto ? 'var(--e3)' : 'var(--e4)' }}>
      {AYUDA.map((t) => (
        <div key={t.titulo}>
          <strong style={{ fontSize: 14, display: 'block', marginBottom: 2 }}>{t.titulo}</strong>
          <p className="nota">{t.cuerpo}</p>
        </div>
      ))}
    </div>
  );
}
